// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceTree.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  const { has, isEmpty, map, partition, values, escapeRegExp, some, matchesProperty, filter } = require('lodash/fp');

  // TODO: This list should be collected from API and it varies per database
  // server types.
  var SourceTypes = {
    TABLE: 'Table',
    VIEW: 'View',
    PROCEDURE: 'Stored Procedure',
    FUNCTION: 'Function'
  };

  /**
   * Check whether query source has name that matches the filter string (non-case
   * sensitive)
   * @param  {String}  filter
   * @return {Boolean} Matched or not
   */
  function isMatchedSource(filter) {
    return function(item) {
      var filterRegex = new RegExp(escapeRegExp(filter.text), 'ig');
      var filteredQuerySourceFields = filterQuerySourceFields(item.querySourceFields, filter, filterRegex);

      // only filter if item not is TypeGroup or querySourceFields is empty
      return !has('type', item) || !isEmpty(filteredQuerySourceFields) || filterRegex.test(item.name);
    };
  }

  /**
   * filter querySourceFields that matches with the filter string
   * @param  {Array}  querySourceFields
   * @param  {String}  filterItem
   * @param  {RegExp}  filterRegex
   * @return {Array}
   */
  function filterQuerySourceFields(querySourceFields, filterItem, filterRegex) {
    return filterItem.isFilterFields
      ? filter(function(field) {
          return filterRegex.test(field.name);
        }, querySourceFields)
      : [];
  }

  /**
   * Recursively create child nodes of a query source node. It allows to auto-group
   * child nodes by its type.
   * @param  {String} filter                        Name-matching filter string
   * @param  {Boolean} autoGroupByType              Group by query source type
   * @param  {Function} querySourceNodeTransformer  Query source node factory
   * @param  {Array} querySources                   Query source array
   * @return {Array}                                Query source node array
   */
  function createChildNodes(filter, autoGroupByType, querySourceNodeTransformer, querySources, parentNode) {
    var match = isMatchedSource(filter);
    var selectSatisfiedSources = querySources.filter(match);
    var childNodes = [];
    var processingSources = selectSatisfiedSources;
    if (autoGroupByType) {
      // Divide query sources into type and non-type category
      // It returns two 2 arrays according to type and non-type category.
      var groups = partition(has('type'), selectSatisfiedSources);
      var typeGroup = groups[0];
      var nonTypeGroup = groups[1];

      if (!isEmpty(typeGroup)) {
        processingSources = nonTypeGroup;

        childNodes = values(SourceTypes)
          .map(function(type) {
            var childNodes = typeGroup.filter(function(item) {
              return item.type === type;
            });
            return {
              id: type,
              name: type,
              group: true,
              parentNode: parentNode,
              querySources: childNodes,
              // just checked Group type if at least 1 child was selected
              checked: childNodes.filter(matchesProperty('checked', true)).length > 0
            };
          })
          .map(querySourceNodeTransformer(filter, false, parentNode));
      }
    }

    var rest = processingSources.map(querySourceNodeTransformer(filter, autoGroupByType, parentNode));

    return childNodes.concat(rest);
  }

  /**
   * Reactive query source -> node transformer
   * @param  {Object} querySource
   * @return {QuerySourceNode}
   */
  var querySourceNodeTransformer = function(filter, autoGroupByType, parentNode, isFilterChecked) {
    return isFilterChecked && !querySource.checked
      ? null
      : function(querySource) {
          return new QuerySourceNode(filter, autoGroupByType, querySource, parentNode);
        };
  };

  /**
   * It is a model of a node in query source tree. It carries the query source
   * instance and relevant UI info.
   * @param {[type]} filter          [description]
   * @param {[type]} autoGroupByType [description]
   * @param {[type]} item            [description]
   */
  function QuerySourceNode(filter, autoGroupByType, item, parentNode) {
    this.dataSource = item;
    var querySources = !isEmpty(item.querySources) ? item.querySources : item.querySourceFields;
    var isFieldNode = !isEmpty(item.querySourceFields);

    var props = {
      id: item.id,
      name: item.name,
      change: item.physicalChange,
      approval: item.approval,
      checked: item.checked,
      parentNode: parentNode,
      childNodes: function() {
        return isEmpty(querySources)
          ? []
          : createChildNodes(filter, autoGroupByType, querySourceNodeTransformer, querySources, this);
      },
      isFieldNode: isFieldNode // use for checking Field Node
    };

    // all data to init node
    item && (this.initData = item);

    m.extendObservable(this, props);
  }

  /**
   * Count child nodes that satisfies passing predication.
   * @param  {Function} predicate
   */
  QuerySourceNode.prototype.childCount = function(predicate) {
    return predicate ? this.childNodes.filter(predicate).length : this.childNodes.length;
  };

  /**
   * set checked property of item
   * @param {Boolean} checked
   */
  QuerySourceNode.prototype.setChecked = function(checked) {
    m.transaction(
      function() {
        this.dataSource.checked = checked;
        this.toggleSelect(checked);
        this.toggleParentSelect(checked);
      }.bind(this)
    );
  };

  /**
   * Toggle selection of current node and its child nodes regarding to
   * the selecting status.
   * @param  {bool} checked Only toggle selection of selecting status.
   */
  QuerySourceNode.prototype.toggleSelect = function(checked) {
    if (!isEmpty(this.childNodes)) {
      var listResult = this.childNodes.filter(matchesProperty('dataSource.checked', !checked)).map(function(child) {
        child.dataSource.checked = checked;
        child.toggleSelect(checked);
      });
      return some(Boolean, listResult);
    }
    return false;
  };

  /**
   * Toggle selection of its parent nodes regarding to the selecting status.
   * @param  {bool} checked Only toggle selection of selecting status.
   */
  QuerySourceNode.prototype.toggleParentSelect = function(checked) {
    var parentNode = this.parentNode;
    if (parentNode) {
      var listResult = parentNode.childNodes.filter(matchesProperty('dataSource.checked', true));
      parentNode.dataSource.checked = listResult.length > 0 ? true : false;
      parentNode.toggleParentSelect && parentNode.toggleParentSelect(checked);
    }
  };

  /**
   * Is typed group node of query source (ie. tables, functions, SPs...)
   * @return {Boolean}
   */
  QuerySourceNode.prototype.isTypeGroup = function() {
    return !!this.dataSource.group;
  };

  /**
   * Tree model of query source. It starts by default root node (id:#root).
   * @param {Array} querySources
   * @param {Bool} isFilterChecked should filter checked or not
   * @param {Bool} isFilterFields should filter querySourceFields or not
   */
  function QuerySourceTree(querySources, isFilterChecked, isFilterFields) {
    // Only take query sources that satisfies predication
    var props = {
      filter: {
        text: '',
        isFilterFields: isFilterFields
      },
      querySources: querySources,
      root: function() {
        return map(
          function(connection) {
            connection.querySources = connection.dbSource.querySources;
            return querySourceNodeTransformer(this.filter, true, isFilterChecked)({
              id: '#root',
              name: 'root',
              querySources: [connection]
            });
          }.bind(this),
          this.querySources
        );
      }
    };

    m.extendObservable(this, props);
  }

  /**
   * Set name-matching filter string for query source tree.
   * Whenever it set, all decendant node's name must satisfy filter string.
   * @param {String} newVal New filter string value.
   */
  QuerySourceTree.prototype.setFilter = function(newVal) {
    this.filter.text = newVal;
  };

  /**
   * set querySources for QuerySourceTree
   * Whenever it set, all QuerySourceNode will be re-calculate
   * @param {Array} querySources
   */
  QuerySourceTree.prototype.setQuerySources = function(querySources) {
    this.querySources = querySources;
  };

  module.exports = QuerySourceTree;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.QuerySourceTreeMultiLevel.js